import { useQuery } from '@tanstack/react-query';
import { apiRequest, apiEnabled } from '../lib/api';
import { useLanguage } from '../i18n/LanguageContext';
import { blogPosts as staticPosts } from '../mock';

// Blog content, API-backed when a backend is configured and falling back to the
// bundled posts otherwise — same approach as use-activities.js.

const STATIC_RESULT = { isLoading: false, isError: false, error: null, isFetching: false };

// The API stores snake_case + i18n objects (collapsed by ?lang=); components
// expect the original camelCase post shape.
function normalizePost(doc) {
  if (!doc || typeof doc !== 'object') return doc;
  return {
    id: doc.slug ?? doc.id,
    slug: doc.slug ?? doc.id,
    title: doc.title,
    excerpt: doc.excerpt,
    content: doc.content,
    seoTitle: doc.seo_title ?? doc.seoTitle,
    seoDescription: doc.seo_description ?? doc.seoDescription,
    image: doc.image,
    category: doc.category,
    date: doc.date,
    readTime: doc.read_time ?? doc.readTime,
    author: doc.author,
    tags: doc.tags || [],
  };
}

export function useBlogPosts() {
  const { lang } = useLanguage();
  const query = useQuery({
    queryKey: ['blog', 'list', lang],
    queryFn: () => apiRequest('/api/blog', { query: { lang, limit: 100 } }),
    enabled: apiEnabled,
    staleTime: 60_000,
  });

  if (!apiEnabled) {
    return { ...STATIC_RESULT, posts: staticPosts, isStatic: true };
  }
  // Keep the bundled posts on screen if the API hiccups, so the blog never
  // renders empty for visitors.
  const items = query.data?.items;
  return {
    posts: items?.length ? items.map(normalizePost) : query.isError ? staticPosts : [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isFetching: query.isFetching,
    isStatic: false,
  };
}

export function useBlogPost(slug) {
  const { lang } = useLanguage();
  const query = useQuery({
    queryKey: ['blog', 'detail', slug, lang],
    queryFn: () => apiRequest(`/api/blog/${encodeURIComponent(slug)}`, { query: { lang } }),
    enabled: apiEnabled && Boolean(slug),
    retry: false,
    staleTime: 60_000,
    // A renamed post resolves its old slug to { redirect_to }, not a doc —
    // pass that through untouched so the page can redirect to the canonical URL.
    select: (doc) => (doc && doc.redirect_to ? doc : normalizePost(doc)),
  });

  const fallback = staticPosts.find((p) => p.id === slug) || null;
  if (!apiEnabled) {
    return { ...STATIC_RESULT, post: fallback, isStatic: true };
  }
  return {
    post: query.data ? query.data : query.isError ? fallback : null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isStatic: false,
  };
}
